import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {SectionCard} from '@/components/section-card'
import {Hint} from '@/components/ui/hint'
import {VistaPills} from '@/components/bateria/vista-pills'
import {ZipFormasControl} from '@/components/bateria/zip-formas-control'
import {SubirEscaneosControl} from '@/components/bateria/subir-escaneos-control'
import {RevisionXlsxControl} from '@/components/bateria/revision-xlsx-control'
import {EscaneosTab} from '@/components/bateria/escaneos-tab'
import {BateriaBitacora} from '@/components/bateria/bateria-bitacora'
import {HelpIcon} from '@/components/help-icon'
import {
  useImprimirFormasPdf,
  type BateriaEvaluacion,
  type BateriaPaquete,
  type IngestaJob,
} from '@/hooks/bateria/use-bateria'
import {translateApiError} from '@/lib/errors'
import {formatDate} from '@/lib/risk'
import type {LotesResumen} from '@/lib/bateria'

export type VistaGestion = 'trabajo' | 'bitacora'

/** Ningún control de acá lleva verde: el único verde vive en la barra de estado, que sabe cuál es el
 * siguiente paso. */
export function GestionTab({
  businessId,
  evaluacionId,
  evaluacion,
  paquetes,
  jobs,
  lotes,
  readOnly,
  vista,
  onVistaChange,
  selectedPaqueteIds,
  onCerrar,
  onEntregar,
}: {
  businessId: string
  evaluacionId: string
  evaluacion: BateriaEvaluacion
  paquetes: BateriaPaquete[]
  jobs: IngestaJob[]
  lotes: LotesResumen
  readOnly: boolean
  vista: VistaGestion
  onVistaChange: (vista: VistaGestion) => void
  selectedPaqueteIds: string[]
  onCerrar: () => void
  onEntregar: () => void
}) {
  const {t} = useTranslation()
  const imprimir = useImprimirFormasPdf(businessId, evaluacionId)
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const faltantes = evaluacion.paquetes_sin_imprimir.length
  const estado = evaluacion.estado

  return (
    <div className="space-y-4">
      <VistaPills
        label={t('admin.bateria.gestion.vista')}
        opciones={[
          {key: 'trabajo', label: t('admin.bateria.gestion.trabajo')},
          {key: 'bitacora', label: t('admin.bateria.gestion.bitacora')},
        ]}
        valor={vista}
        onPick={onVistaChange}
        testIdPrefix="gestion-sub"
      />

      {vista === 'bitacora' ? (
        <BateriaBitacora businessId={businessId} evaluacionId={evaluacionId} enabled />
      ) : (
        <>
          <div id="lote-cuadernillos" className="scroll-mt-4">
            <SectionCard
              title={
                <span className="inline-flex items-center gap-1.5">
                  {t('admin.bateria.gestion.cuadernillos.title')}
                  <HelpIcon
                    label={t('common.help.iconLabelFor', {campo: t('admin.bateria.gestion.cuadernillos.title')})}
                    testId="gestion-cuadernillos-tip">
                    {t('admin.bateria.gestion.cuadernillos.tip')}
                  </HelpIcon>
                </span>
              }
              testId="gestion-cuadernillos"
              bodyClassName="space-y-3 px-5 py-4">
              <ZipFormasControl
                businessId={businessId}
                evaluacionId={evaluacionId}
                evaluacion={evaluacion}
                selectedPaqueteIds={selectedPaqueteIds}
                hasPaquetes={paquetes.length > 0}
              />
              {evaluacion.zip_formas_generado_en && (
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span data-testid="banda-zip">
                    {t('admin.bateria.bandaDeVerdad.zip', {
                      fecha: formatDate(evaluacion.zip_formas_generado_en),
                      impresas: paquetes.length - faltantes,
                      total: paquetes.length,
                    })}
                  </span>
                  {faltantes > 0 && !readOnly && (
                    <Button
                      size="xs"
                      variant="outline"
                      data-testid="banda-imprimir-faltantes"
                      disabled={imprimir.isPending}
                      onClick={() => {
                        setError(null)
                        setAviso(null)
                        imprimir.mutate(
                          {sinImprimir: true},
                          {
                            onError: (err) => setError(translateApiError(err)),
                            onSuccess: (r) =>
                              setAviso(r.zipEncolado ? t('admin.bateria.bandaDeVerdad.zipEncolado') : null),
                          }
                        )
                      }}>
                      {t('admin.bateria.bandaDeVerdad.imprimirFaltantes', {count: faltantes})}
                    </Button>
                  )}
                </div>
              )}
              {error && <p className="text-xs text-destructive">{error}</p>}
              {aviso && <Hint>{aviso}</Hint>}
            </SectionCard>
          </div>

          <div id="lote-escaneos" className="scroll-mt-4">
            <SectionCard
              title={
                <span className="inline-flex items-center gap-1.5">
                  {t('admin.bateria.gestion.escaneos.title')}
                  <HelpIcon
                    label={t('common.help.iconLabelFor', {campo: t('admin.bateria.gestion.escaneos.title')})}
                    testId="gestion-escaneos-tip">
                    {t('admin.bateria.gestion.escaneos.tip')}
                  </HelpIcon>
                </span>
              }
              testId="gestion-escaneos"
              bodyClassName="space-y-4 px-5 py-4">
              <EscaneosTab businessId={businessId} evaluacionId={evaluacionId} jobs={jobs} lotes={lotes} />
              {!readOnly && (
                <div className="space-y-1.5 border-t border-dashed pt-3">
                  <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                    {t('admin.bateria.gestion.subirZip')}
                  </p>
                  <SubirEscaneosControl
                    businessId={businessId}
                    evaluacionId={evaluacionId}
                    paquetes={paquetes}
                    lotes={lotes}
                    variant="bulk"
                  />
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard
            title={
              <span className="inline-flex items-center gap-1.5">
                {t('admin.bateria.gestion.herramientas.title')}
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('admin.bateria.gestion.herramientas.title')})}
                  testId="gestion-herramientas-tip">
                  {t('admin.bateria.gestion.herramientas.tip')}
                </HelpIcon>
              </span>
            }
            testId="gestion-herramientas"
            bodyClassName="space-y-2 px-5 py-4">
            <RevisionXlsxControl
              businessId={businessId}
              evaluacionId={evaluacionId}
              selectedPaqueteIds={selectedPaqueteIds}
              readOnly={readOnly}
            />
          </SectionCard>

          {(estado === 'abierta' || estado === 'cerrada') && (
            <SectionCard
              title={t('admin.bateria.gestion.ciclo.title')}
              testId="gestion-ciclo"
              className="border-t-2 border-border"
              bodyClassName="space-y-1.5 px-5 py-4">
              {estado === 'abierta' ? (
                <Button size="sm" variant="outline" data-testid="cerrar-evaluacion" onClick={onCerrar}>
                  {t('admin.bateria.lifecycle.cerrar')}
                </Button>
              ) : (
                <Button size="sm" variant="outline" data-testid="entregar-evaluacion" onClick={onEntregar}>
                  {t('admin.bateria.lifecycle.entregar')}
                </Button>
              )}
              {/* T15: un control irreversible lleva su marca visible, pegada al botón. */}
              <Hint testId="gestion-ciclo-hint">{t('admin.bateria.gestion.ciclo.hint')}</Hint>
            </SectionCard>
          )}
        </>
      )}
    </div>
  )
}
